import { gql } from "config/graphql";
import { MutationResponse } from "config/types";
import { getApolloClient } from "store/apollo";
import { assertDefined } from "utils/error";

interface IInitiatePasswordResetData {
  initiatePasswordReset: {
    response: {
      success: boolean;
    };
  };
}

interface IResetForgottenPasswordData {
  resetForgottenPassword: {
    clientMutationId: string | null;
  };
}

export interface ICreateCredentialsInput {
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
}

export interface ICreateCredentialData {
  createCredential: {
    credential: {
      status: "pending" | "confirmed" | "rejected" | "decommissioned" | "transient";
      token: {
        accessToken: string;
        createdAt: string;
        refreshToken: string;
      };
    };
  };
}

/* ------------     MUTATIONS    ------------------ */

export const initiatePasswordResetMutation: MutationResponse<
  string,
  IInitiatePasswordResetData
> = async (email: string) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation initiatePasswordReset($input: InitiatePasswordResetInput!) {
      initiatePasswordReset(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<IInitiatePasswordResetData>({
    variables: {
      input: { email },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export const passwordMutation = async (
  token: string,
  password: string
): Promise<{ data: IResetForgottenPasswordData }> => {
  const client = getApolloClient();

  const mutation = gql`
    mutation resetForgottenPassword($token: String!, $password: String!) {
      resetForgottenPassword(
        input: { token: $token, password: $password, passwordConfirmation: $password }
      ) {
        clientMutationId
      }
    }
  `;

  const { data } = await client.mutate<IResetForgottenPasswordData>({
    variables: { token, password },
    mutation,
  });
  return { data: assertDefined(data) };
};

export const createCredential: MutationResponse<
  ICreateCredentialsInput & { reauthenticate: boolean },
  ICreateCredentialData
> = async (credentials: ICreateCredentialsInput & { reauthenticate: boolean }) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createCredential($input: CreateCredentialInput!) {
      createCredential(input: $input) {
        credential {
          status
          token {
            createdAt
            accessToken
            refreshToken
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreateCredentialData>({
    variables: { input: credentials },
    mutation,
  });
  return { data: assertDefined(data) };
};
